import React from "react";
import { ReactComponent as BikeIcon } from "../../../assets/icons/dash/bike.svg";
import { ReactComponent as BusIcon } from "../../../assets/icons/dash/bus.svg";
import { ReactComponent as CarIcon } from "../../../assets/icons/dash/car.svg";
import { ReactComponent as TriIcon } from "../../../assets/icons/dash/tri.svg";
import { ReactComponent as TruckIcon } from "../../../assets/icons/dash/truck.svg";
import { ReactComponent as WheelIcon } from "../../../assets/icons/dash/Wheel.svg";
import { Separator } from "../../../components/ui/common";
import DashboardCard from "../../../components/ui/dashboard/DashboardCard";
import { useGetVehicleStatsQuery } from "../vehicleApiSlice";

// Icon mapping configuration
const vehicleIconConfig = {
  motorcycle: {
    icon: <BusIcon className="h-[31.7px] w-[31.7px]" />,
    iconClassName: "bg-[#EDF2DFB2]",
    label: "Motorcycles",
  },
  car: {
    icon: <CarIcon className="h-[31.7px] w-[31.7px]" />,
    iconClassName: "bg-[#E8BB001A]",
    label: "Cars",
  },
  bus: {
    icon: <TruckIcon className="h-[31.7px] w-[31.7px]" />,
    iconClassName: "bg-[#0593481A]",
    label: "Buses",
  },
  bicycle: {
    icon: <BikeIcon className="h-[31.7px] w-[31.7px]" />,
    iconClassName: "bg-[#FB37481A]",
    label: "Bicycles",
  },
  truck: {
    icon: <TriIcon className="h-[31.7px] w-[31.7px]" />,
    iconClassName: "bg-[#990E7A1A]",
    label: "Trucks",
  },
  tricycle: {
    icon: <TriIcon className="h-[31.7px] w-[31.7px]" />,
    iconClassName: "bg-[#990E7A1A]",
    label: "Tricycles",
  },
} as const;

const VehicleDashStats: React.FC = () => {
  const { data, isLoading, isSuccess, isError, error } =
    useGetVehicleStatsQuery();

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-4 sm:gap-x-10 sm:gap-y-6 bg-white rounded-[10px] p-4">
        {/* Total vehicles skeleton */}
        <div className="flex flex-col items-center space-y-2">
          <div className="animate-pulse bg-gray-200 h-12 w-12 rounded-full"></div>
          <div className="animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
          <div className="animate-pulse bg-gray-200 h-4 w-20 rounded"></div>
        </div>

        <div className="hidden sm:block bg-[#E2E2E2] h-[54px] w-[2px]"></div>

        {/* Vehicle type skeletons */}
        {Array.from({ length: 5 }).map((_, index) => (
          <React.Fragment key={`skeleton-${index}`}>
            <div className="flex flex-col items-center space-y-2">
              <div className="animate-pulse bg-gray-200 h-12 w-12 rounded-full"></div>
              <div className="animate-pulse bg-gray-200 h-6 w-12 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-4 w-16 rounded"></div>
            </div>
            {index < 4 && <div className="hidden sm:block bg-[#E2E2E2] h-[54px] w-[2px]"></div>}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // Handle error state
  if (isError) {
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-4 sm:gap-x-10 sm:gap-y-6 bg-white rounded-[10px] p-4">
        <div className="text-red-500">Error loading vehicle statistics</div>
      </div>
    );
  }

  // Calculate total vehicles across all types
  const totalVehicles =
    data?.data?.reduce((sum, vehicle) => sum + vehicle.totalVehicles, 0) || 0;

  // Get vehicle stats by type for easier access
  const getVehicleStatsByType = (type: string) => {
    return data?.data?.find(
      (vehicle) => vehicle.vehicleType.toLowerCase() === type.toLowerCase()
    );
  };

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-4 sm:gap-x-10 sm:gap-y-6 bg-white rounded-[10px] p-4">
      {/* Total Vehicles Card */}
      <DashboardCard
        icon={<WheelIcon className="h-[31.7px] w-[31.7px]" />}
        iconClassName="bg-[#E7F2F5]"
        value={totalVehicles}
        label="Total vehicles"
        width="w-[150px]"
      />

      <Separator />

      {/* Dynamic Vehicle Type Cards */}
      {data?.data?.map((vehicleStats, index) => {
        const vehicleTypeKey =
          vehicleStats.vehicleType.toLowerCase() as keyof typeof vehicleIconConfig;
        const config = vehicleIconConfig[vehicleTypeKey];

        const fallbackConfig = {
          icon: <WheelIcon className="h-[31.7px] w-[31.7px]" />,
          iconClassName: "bg-[#F5F5F5]",
          label: vehicleStats.vehicleType,
        };

        const currentConfig = config || fallbackConfig;

        return (
          <React.Fragment key={vehicleStats.vehicleType}>
            <DashboardCard
              icon={currentConfig.icon}
              iconClassName={currentConfig.iconClassName}
              value={vehicleStats.totalVehicles}
              label={currentConfig.label}
              width="w-[106px]"
            />

            {index < data.data.length - 1 && <Separator />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default VehicleDashStats;
